import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MAXW } from './theme';
import { MapPin, Search, X } from './icons';
import { scrollToSection } from './scrollToSection';
import { NavbarAuth } from './NavbarAuth';
import { CityChangeModal } from '@/features/location/components/CityChangeModal';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { getAccessToken } from '@/shared/lib/storage/token';
import { setCity, setSearch } from '@/shared/store/slices/filtersSlice';

const BRAND_LOGO_SRC = '/screenista-logo.svg';

type NavbarProps = {
  homeHref?: string;
  city?: string;
  authMode?: 'split' | 'signin';
};

export function Navbar({
  homeHref,
  city,
  authMode = 'split',
}: NavbarProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const storeCity = useAppSelector((s) => s.filters.city);
  const storeSearch = useAppSelector((s) => s.filters.search);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const hasToken = Boolean(getAccessToken()) || isAuthenticated;
  const cityLabel = storeCity || city || 'Bhopal';
  const [query, setQuery] = useState(storeSearch);
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [searchError, setSearchError] = useState(false);

  useEffect(() => {
    setQuery(storeSearch);
  }, [storeSearch]);

  const goHome = () => {
    if (homeHref) {
      navigate(homeHref);
      return;
    }
    scrollToSection('top');
  };

  const clearSearch = () => {
    setQuery('');
    setSearchError(false);
    dispatch(setSearch(''));
  };

  const runSearch = (event?: { preventDefault?: () => void }) => {
    event?.preventDefault?.();
    const keyword = query.trim();
    if (!keyword) {
      setSearchError(true);
      return;
    }
    setSearchError(false);
    dispatch(setSearch(keyword));
    if (location.pathname !== '/billboards') {
      navigate('/billboards');
    }
  };

  const handleCitySelect = (nextCity: string) => {
    dispatch(setCity(nextCity));
    setCityModalOpen(false);
    if (location.pathname !== '/billboards' && location.pathname !== '/') {
      navigate('/billboards');
    }
  };

  return (
    <>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(230, 238, 251, 0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: 'none',
          boxShadow: 'none',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        <style>{`
          .landing-brand-logo {
            display: block;
            height: 40px;
            width: auto;
            max-width: min(190px, 42vw);
            object-fit: contain;
          }
          .landing-nav-grad-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 12px 24px -6px rgba(80, 60, 170, 0.3) !important;
          }
          .landing-nav-auth {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-shrink: 0;
          }
          .landing-nav-auth-signin {
            padding: 10px 18px;
            border-radius: 999px;
            font-weight: 700;
            font-size: 14px;
            color: #3A3660;
            background: none;
            border: none;
            cursor: pointer;
            font-family: 'Plus Jakarta Sans', sans-serif;
            white-space: nowrap;
          }
          .landing-nav-auth-signup,
          .landing-nav-auth-dashboard {
            padding: 10px 22px;
            border-radius: 999px;
            font-weight: 700;
            font-size: 14px;
            color: #fff;
            background: linear-gradient(135deg, #3B66B8 0%, #8156B4 100%);
            border: none;
            cursor: pointer;
            font-family: 'Plus Jakarta Sans', sans-serif;
            white-space: nowrap;
            box-shadow: 0 8px 18px -10px rgba(80, 60, 170, 0.4);
            transition: transform .2s ease, box-shadow .2s ease;
          }
          @media (max-width: 980px) {
            .landing-nav-search { display: none !important; }
          }
          @media (max-width: 860px) {
            .landing-brand-logo { height: 34px; max-width: min(150px, 40vw); }
            .landing-nav { padding: 12px 24px !important; gap: 12px !important; }
            .landing-nav-auth { gap: 6px; }
            .landing-nav-auth-signin { padding: 8px 10px; font-size: 13px; }
            .landing-nav-auth-signup,
            .landing-nav-auth-dashboard { padding: 8px 14px; font-size: 13px; }
          }
          @media (max-width: 480px) {
            .landing-brand-logo { height: 28px; max-width: min(118px, 38vw); }
            .landing-nav { padding: 10px 16px !important; gap: 8px !important; }
            .landing-nav-auth { gap: 4px; }
            .landing-nav-auth-signin { padding: 7px 8px; font-size: 12px; }
            .landing-nav-auth-signup,
            .landing-nav-auth-dashboard { padding: 7px 12px; font-size: 12px; border-radius: 8px; }
          }
          @media (max-width: 360px) {
            .landing-brand-logo { height: 26px; max-width: min(104px, 36vw); }
            .landing-nav-auth-signin { padding: 6px 6px; }
            .landing-nav-auth-signup,
            .landing-nav-auth-dashboard { padding: 6px 10px; }
          }
        `}</style>
        <nav
          className="landing-nav"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: MAXW,
            margin: '0 auto',
            padding: '14px 24px',
            gap: 20,
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <button
            type="button"
            onClick={goHome}
            aria-label="Screenista home"
            className="landing-brand"
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              flex: '0 1 auto',
              minWidth: 0,
              maxWidth: '52%',
            }}
          >
            <img
              src={BRAND_LOGO_SRC}
              alt="Screenista"
              className="landing-brand-logo"
            />
          </button>

          <form
            className="landing-nav-search"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              runSearch(event);
            }}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              maxWidth: 440,
              background: 'rgba(255, 255, 255, 0.96)',
              border: searchError
                ? '1px solid #DC2626'
                : '1px solid #ECEAF5',
              boxShadow: searchError
                ? '0 0 0 3px rgba(220, 38, 38, 0.12)'
                : '0 10px 30px rgba(60, 40, 120, 0.05)',
              borderRadius: 999,
              padding: '5px 6px 5px 6px',
              transition: 'border-color .2s ease, box-shadow .2s ease',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            <button
              type="button"
              onClick={() => setCityModalOpen(true)}
              aria-label="Change city"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                fontWeight: 700,
                padding: '8px 14px',
                borderRadius: 999,
                background: '#F3F2F9',
                color: '#3A3660',
                whiteSpace: 'nowrap',
                border: 'none',
                cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              <MapPin size={14} color="#8156B4" /> {cityLabel}
            </button>
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                color: '#6A6688',
                fontSize: 14,
              }}
            >
              <Search size={16} />
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (searchError) setSearchError(false);
                }}
                placeholder="Search screens..."
                aria-invalid={searchError}
                enterKeyHint="search"
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  width: '100%',
                  fontSize: 14,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: '#3A3660',
                }}
              />
              {query ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={clearSearch}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    border: 'none',
                    background: 'rgba(10,14,39,0.06)',
                    color: '#6A6688',
                    cursor: 'pointer',
                    flexShrink: 0,
                    padding: 0,
                  }}
                >
                  <X size={12} />
                </button>
              ) : null}
            </div>
            <button
              type="submit"
              className="landing-nav-grad-btn"
              style={{
                background: 'linear-gradient(135deg, #3B66B8 0%, #8156B4 100%)',
                color: '#fff',
                padding: '9px 20px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 700,
                border: 'none',
                cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                boxShadow: '0 8px 18px -10px rgba(80, 60, 170, 0.4)',
                transition: 'transform .2s ease, box-shadow .2s ease',
              }}
            >
              Search
            </button>
          </form>

          <div className="landing-nav-auth">
            <NavbarAuth
              hasToken={hasToken}
              authMode={authMode}
              onDashboard={() => navigate('/dashboard')}
              onLogin={() => navigate('/login')}
              onSignup={() => navigate('/signup')}
            />
          </div>
        </nav>
      </header>

      <CityChangeModal
        open={cityModalOpen}
        selectedCity={cityLabel}
        onClose={() => setCityModalOpen(false)}
        onSelect={handleCitySelect}
      />
    </>
  );
}
